/* eslint-disable no-nested-ternary */
import { v4 as uuidv4 } from "uuid";
import qs from "qs";
import { getCachedCategory } from "../graphql/queries/categories";
import {
  MINI_CAT_ICONS_MAP,
  SPEC_ICONS,
  SPEC_VALUES,
  URL_LEVELS,
} from "../common/constants";
import searchFields from "./searchFields";
import categories from "./categories";
import fields from "./fields";
import config from "../common/config";

/**
 * Remove the last / from the url string
 * @param {string} str
 * @return {string}
 */
export const stripTrailingSlash = (str = "") => {
  if (str.substr(-1) === "/") {
    return str.substr(0, str.length - 1);
  }
  return str;
};

const getSpecValue = (spec, value) =>
  SPEC_VALUES[spec]
    ? `${SPEC_VALUES[spec][value]} ${fields[spec].unit || ""} `
    : `${value} ${fields[spec].unit || ""}`;

export const getSpecs = (listing, categoryIconsMap = MINI_CAT_ICONS_MAP) => {
  const relevantSpecs = categoryIconsMap[listing.category];
  if (relevantSpecs) {
    return relevantSpecs.reduce((acc, spec) => {
      if (listing[spec]) {
        acc.push({
          icon: SPEC_ICONS[spec],
          value: `${getSpecValue(spec, listing[spec])}${
            !SPEC_ICONS[spec] ? " | " : ""
          }`,
        });
      }
      return acc;
    }, []);
  }

  return [];
};

export const financialFormat = (num, unit = "") => {
  const value = (num || "")
    .toString()
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  return unit ? `${value} ${unit}` : value;
};

export const matchNumber = (str) => new RegExp("^[0-9]+$").test(str);

export const castInputToGraphQLTypes = (query) => {
  Object.keys(query).forEach((key) => {
    if (fields[key]) {
      switch (fields[key].graphqlType) {
        case "Float":
          query[key] = parseFloat(query[key]);
          break;
        case "Int":
          query[key] =
            typeof query[key] === "boolean"
              ? Number(query[key])
              : parseInt(query[key], 10);
          break;
        default:
          break;
      }
    }
  });

  return query;
};

// TODO don't hard code city names. Move them to config 'cities with directions'
export const matchCityWithDirection = (str) =>
  new RegExp(`^(الرياض|جدة)$`).test(str);

export const parseListingID = (uri) => {
  const matches = uri?.match(/(\d)\w+/g);
  return matches?.length ? matches[matches.length - 1] : null;
};

/**
 * Returns an object describing the level of the path: category, city, direction, district ot listing
 * @param {array} pathParams
 * @return {{level: string, page: number | null, cityName?: string, districtName?: string, listingURI?: string, directionName?: string}}
 */
// eslint-disable-next-line consistent-return
export const getPathLevel = (pathParams) => {
  const pageNumber = parseInt(pathParams[pathParams.length - 1], 10) || 1;
  if (pathParams.length === 1) {
    if (matchNumber(pathParams[0])) {
      return {
        level: URL_LEVELS.CATEGORY,
        page: pageNumber,
      };
    }

    return {
      level: URL_LEVELS.CITY,
      cityName: pathParams[0],
      page: pageNumber,
    };
  }

  if (pathParams.length === 2) {
    if (matchNumber(pathParams[1])) {
      return {
        level: URL_LEVELS.CITY,
        cityName: pathParams[0],
        page: pageNumber,
      };
    }

    if (matchCityWithDirection(pathParams[0])) {
      return {
        level: URL_LEVELS.DIRECTION,
        cityName: pathParams[0],
        directionName: pathParams[1],
        page: pageNumber,
      };
    }

    return {
      level: URL_LEVELS.DISTRICT,
      cityName: pathParams[0],
      districtName: pathParams[1],
      page: pageNumber,
    };
  }

  if (pathParams.length === 3) {
    if (matchNumber(pathParams[2]) && matchCityWithDirection(pathParams[0])) {
      return {
        level: URL_LEVELS.DIRECTION,
        cityName: pathParams[0],
        directionName: pathParams[1],
        page: pageNumber,
      };
    }

    if (matchNumber(pathParams[2]) && !matchCityWithDirection(pathParams[0])) {
      return {
        level: URL_LEVELS.DISTRICT,
        cityName: pathParams[0],
        districtName: pathParams[1],
        page: pageNumber,
      };
    }

    if (matchCityWithDirection(pathParams[0])) {
      return {
        level: URL_LEVELS.DISTRICT,
        cityName: pathParams[0],
        directionName: pathParams[1],
        districtName: pathParams[2],
        page: pageNumber,
      };
    }

    return {
      level: URL_LEVELS.LISTING,
      cityName: pathParams[0],
      districtName: pathParams[1],
      listingURI: pathParams[2],
      id: parseListingID(pathParams[2]),
      page: null,
    };
  }

  if (pathParams.length === 4) {
    if (matchNumber(pathParams[3])) {
      return {
        level: URL_LEVELS.DISTRICT,
        cityName: pathParams[0],
        directionName: pathParams[1],
        districtName: pathParams[2],
        page: pageNumber,
      };
    }
    return {
      level: URL_LEVELS.LISTING,
      cityName: pathParams[0],
      directionName: pathParams[1],
      districtName: pathParams[2],
      listingURI: pathParams[3],
      id: parseListingID(pathParams[3]),
      page: null,
    };
  }
};

export const getStatsLevel = (pathParams) => {
  if (pathParams.length < 3) {
    return {
      level: URL_LEVELS.CITY,
      categoryName: pathParams[0],
      cityName: pathParams[1] || "الرياض",
    };
  }

  if (matchCityWithDirection(pathParams[1])) {
    return {
      level: URL_LEVELS.DIRECTION,
      categoryName: pathParams[0],
      cityName: pathParams[1],
      directionName: pathParams[2],
      districtName: pathParams.length === 4 ? pathParams[3] : "",
    };
  }

  return {
    level: URL_LEVELS.DISTRICT,
    categoryName: pathParams[0],
    cityName: pathParams[1],
    districtName: pathParams[2],
  };
};

export const getDistrictReviewLevel = (pathParams) => {
  if (pathParams.length === 2) {
    return {
      level: URL_LEVELS.DIRECTION,
      directionName: pathParams[0],
      districtName: pathParams[1],
    };
  }

  return {
    level: URL_LEVELS.DISTRICT,
    districtName: pathParams[0],
  };
};

export const getRandomItem = (arr) => {
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
};

/**
 * Build path from pathParams object by replacing {placeholders} by their values
 * @param {string} path
 * @param {object} pathParams
 * @return {string}
 */
export const buildPath = ({ path, pathParams }) => {
  const url = path.replace(/\{([\w-]+)\}/g, (fullMatch, key) => {
    let value;
    // this is the "same" as pathParams.hasOwnProperty(key), just safer, as of eslint recommendation.
    if (Object.prototype.hasOwnProperty.call(pathParams, key)) {
      value =
        pathParams[key] === undefined || pathParams[key] === null
          ? ""
          : pathParams[key].toString();
    } else {
      value = "";
    }
    return value;
  });
  // remove empty strings
  return url.split("/").filter(Boolean).join("/");
};

/**
 * Given an array of listings, returns the first element of every listing.imgs for the `limit`-first elements
 * @param arr
 * @param limit
 */
export const getPreloadedImage = (arr, limit) =>
  arr
    .slice(0, limit)
    .map((item) => {
      return Array.isArray(item.imgs) && item.imgs[0];
    })
    .filter(Boolean);

export const buildImageSrc = (imgSrc, baseURL = config.IMAGES_BASE_URL) =>
  `${stripTrailingSlash(baseURL)}/${imgSrc}`;

function getSpecsValue(listing, extendedValues = {}, item) {
  const { fieldName, value, extended, ...rest } = item;
  const objValue = listing[fieldName] || extendedValues[fieldName];
  let specValue;
  if (!value) {
    specValue = objValue;
  } else if (typeof value === "function") {
    specValue = objValue
      ? value(objValue, { ...listing, ...extendedValues })
      : objValue;
  } else if (objValue) {
    specValue = value;
  }
  if (specValue && specValue !== "0") {
    return { ...rest, value: specValue };
  }

  if (extended) {
    return { ...rest };
  }
  return null;
}

export const buildSpecsTable = (listing, relevantSpecs, extendedValues) => {
  const specs = [];

  if (listing?.plan_no || listing?.parcel_no) {
    specs.push({
      label: "المخطط و القطعة",
      value: `${listing.plan_no ?? ""} - ${listing.parcel_no ?? ""}`,
      fieldName: "plan_no",
    });
  }

  relevantSpecs.forEach((item) => {
    const { extended, nestedItems } = item;
    const specValue = getSpecsValue(listing, extendedValues, item);
    if (extended) {
      const nestedSpec = [];
      nestedItems.forEach((nested) => {
        const nestedSpecValue = getSpecsValue(listing, extendedValues, nested);
        if (nestedSpecValue) nestedSpec.push(nestedSpecValue);
      });
      if (specValue || nestedSpec) specs.push({ ...specValue, nestedSpec });
    } else if (specValue) {
      specs.push({ ...specValue });
    }
  });
  return specs;
};

/**
 * Safely returns the env variable {name} from process. Throws error if it does not exist.
 * @param {string} name
 * @return {string}
 */
export const env = (name) => {
  const value = process.env[name];

  if (!value) {
    throw new Error(`Missing: process.env.${name}`);
  }

  return value;
};

/**
 * Safely cehck if the provided phone number is saudi number.
 * @param {string|number} phone
 * @return {boolean}
 */
export const isSaudiNumber = (phone = "") => {
  const regex = new RegExp(/^(009665|9665|\+9665|05|5)([0-9]{8})$/);
  return regex.test(phone);
};

/**
 * Safely cehck if the provided pw is a valid password for Aqar.
 * @param {string} pw
 * @return {boolean}
 */
export const isValidPassword = (pw = "") => {
  const regex = new RegExp(/^(?=(.*[0-9]))(?=.*[A-z])(?=(.*)).{8,}/);
  return regex.test(pw);
};

/**
 * Safely cehck if the provided email is a valid email.
 * @param {string} email
 * @return {boolean}
 */
export const isValidEmail = (email = "") => {
  const regex = new RegExp(/\S+@\S+\.\S+/);
  return regex.test(email);
};

export const pluralMonths = (months) => {
  if (months <= 10 && months >= 3) {
    return "أشهر";
  }
  return "شهر";
};

export const pluralYears = (years) => {
  if (years <= 10 && years >= 3) {
    return "سنوات";
  }
  return "سنة";
};

export const getDeviceID = () => {
  if (typeof window !== "undefined") {
    const reqDeviceToken = localStorage.getItem("req-device-token");
    if (reqDeviceToken) {
      return reqDeviceToken;
    }
    const deviceID = uuidv4();
    localStorage.setItem("req-device-token", deviceID);
    return deviceID;
  }
  return null;
};

export const prefixWithZero = (str) => {
  if (str[0] !== "0" && str[0] === "5") {
    return `0${str}`;
  }
  return str;
};

const getRentPeriodText = (rentPeriodID) => {
  switch (rentPeriodID) {
    case 1:
      return "يومي";
    case 2:
      return "شهري";
    case 3:
      return "سنوي";
    default:
      return "";
  }
};

export function getFullPriceText(price, rentPeriod) {
  const rentPeriodText = getRentPeriodText(rentPeriod);
  return `${
    rentPeriodText ? ` ريال سعودي / ${rentPeriodText} ` : " ريال سعودي "
  }${financialFormat(price)}`;
}

export const getWhereValue = (key, searchParams) => {
  key =
    key === "price" && Object.keys(searchParams)[0] === "price_category"
      ? "villaPrice"
      : key;
  const field = searchFields[key];
  const value = Object.values(searchParams)[0];
  switch (field.type) {
    case "two-input-range": {
      const [min, max] = value?.split("-") || [];
      return min && max
        ? {
            gte: min,
            lte: max,
          }
        : min
        ? {
            gte: min,
          }
        : max
        ? {
            lte: max,
          }
        : undefined;
    }
    case "radio":
      return value ? { eq: value } : undefined;
    default:
      return field?.options.ar?.find((opt) => opt.value === value)?.where;
  }
};

export const searchStrToWhereFilters = ({
  apolloClient,
  categoryName,
  searchQuery,
  isDailyRentPage,
}) => {
  const { id: categoryId } = isDailyRentPage
    ? { id: "daily-rent" }
    : getCachedCategory(apolloClient, categoryName) || {};
  // TODO: this needs to be improved.
  return Object.keys(searchQuery).reduce((acc, current) => {
    if (isDailyRentPage && current === "price_range") return acc;
    const FieldsExistsforCat = categories[categoryId]?.search.base
      .concat(categories[categoryId]?.search.more)
      .map((item) => searchFields[item])
      .find((filter) => filter?.urlName === current);

    return FieldsExistsforCat
      ? {
          ...acc,
          [FieldsExistsforCat.elasticName]: getWhereValue(
            FieldsExistsforCat.key,
            { [current]: searchQuery[current] }
          ),
        }
      : acc;
  }, {});
};

export const getDailyRentFilter = (query) => {
  const [start, end] = query?.day_date?.split("-") || [];
  const [min, max] = query?.price_range?.split("-") || [];
  return {
    availability: { eq: 1 },
    ...(start || end ? { day_date: { gte: start, lte: end } } : {}),
    ...(min || max
      ? {
          range_price: {
            ...(min ? { gte: min } : {}),
            ...(max ? { lte: max } : {}),
          },
        }
      : {}),
  };
};

export const getStoredCalledListingsKeys = () =>
  Object.keys(localStorage).filter((key) => key.match(/\w*-called-listing/));

export const getStoredCalledListings = (keys) => {
  keys = keys || getStoredCalledListingsKeys();

  return keys
    .map((key) => JSON.parse(localStorage.getItem(key)))
    .sort((a, b) => b.timestamp - a.timestamp);
};

export const storeCalledListing = (listing) => {
  const timestampedListing = { ...listing, timestamp: Date.now() };
  const listingKey = `${listing.id}-called-listing`;

  const localStorageListing = localStorage.getItem(listingKey);

  if (!localStorageListing) {
    // const localStorageCalledListingsKeys = Object.keys(
    //   localStorage
    // ).filter((key) => key.match(/\w*-called-listing/));

    const localStorageCalledListingsKeys = getStoredCalledListingsKeys();

    const isMaxReached = localStorageCalledListingsKeys.length > 50;

    if (isMaxReached) {
      // const sortedLocalStorageCalledListings = localStorageCalledListingsKeys
      //   .map((key) => JSON.parse(localStorage.getItem(key)))
      //   .sort((a, b) => b.timestamp - a.timestamp);

      const sortedLocalStorageCalledListings = getStoredCalledListings(
        localStorageCalledListingsKeys
      );

      const lastElementId =
        sortedLocalStorageCalledListings[
          sortedLocalStorageCalledListings.length - 1
        ].id;

      const lastElementKey = `${lastElementId}-called-listing`;
      localStorage.removeItem(lastElementKey);

      localStorage.setItem(listingKey, JSON.stringify(timestampedListing));
    } else {
      localStorage.setItem(listingKey, JSON.stringify(timestampedListing));
    }
  } else {
    localStorage.setItem(listingKey, JSON.stringify(timestampedListing));
  }
};

/**
 * Returns an object with fields the ids array items. Exp: ids = [1, 2, 3] it returns {1:true, 2:true, 3:true}
 * @param ids
 * @param timestamp
 * @return {object}
 */
export const buildFavListingsObj = (ids, timestamp) =>
  ids.reduce((acc, id) => ({ ...acc, [id]: true }), {
    lastSync: timestamp,
  });

export const storeFavListingIDs = (favObject) => {
  localStorage.setItem("favListings", JSON.stringify(favObject));
};

export const getStoredFavListingIDs = () =>
  JSON.parse(localStorage.getItem("favListings"));

export const getLastFavIDsSync = () => {
  const favObject = getStoredFavListingIDs();

  return favObject?.lastSync;
};

export const addFavListingID = (id) => {
  const favObject = getStoredFavListingIDs();
  const newFavObject = { ...favObject, [id]: true };
  storeFavListingIDs(newFavObject);
};

export const removeFavListingID = (id) => {
  const { [id]: removed, ...rest } = getStoredFavListingIDs();
  storeFavListingIDs(rest);
};

export const storeImagsReportedIDs = (reportedObject) => {
  localStorage.setItem("imgsReported", JSON.stringify(reportedObject));
};

export const getStoredImgsReportedIDs = () =>
  JSON.parse(localStorage.getItem("imgsReported"));

export const addImgReportedID = (img) => {
  const reportedObject = getStoredImgsReportedIDs();
  const newReportedObject = { ...reportedObject, [img]: true };
  storeImagsReportedIDs(newReportedObject);
};

export const getSavedPolygon = () => {
  try {
    return JSON.parse(localStorage.getItem("lastPolygon"));
  } catch (_) {
    return localStorage.getItem("lastPolygon");
  }
};

export const storeVisitedMarkersIDs = (markersObject) => {
  localStorage.setItem("visitedMarkers", JSON.stringify(markersObject));
};

export const getStoredVisitedMarkersIDs = () =>
  JSON.parse(localStorage.getItem("visitedMarkers")) || {};

export const addVisitedMarkerID = (id) => {
  const markerObject = getStoredVisitedMarkersIDs();
  const newMarkerObject = { ...markerObject, [id]: true };
  storeVisitedMarkersIDs(newMarkerObject);
};

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
}

export const isPointsClose = (p1, p2, threshold = 0.2) => {
  if (!p1 || !p2) {
    return false;
  }

  const { lat: lat1, lng: lng1 } = p1;
  const { lat: lat2, lng: lng2 } = p2;

  if (
    typeof lat1 !== "number" ||
    typeof lat2 !== "number" ||
    typeof lng1 !== "number" ||
    typeof lng2 !== "number"
  ) {
    return false;
  }

  const distance = getDistanceFromLatLonInKm(lat1, lng2, lat2, lng2);

  return distance < threshold;
};

export const insidePolygon = (point, polygon) => {
  const x = point[0];
  const y = point[1];
  let inside = false;
  // eslint-disable-next-line no-plusplus
  for (let i = 0, j = polygon?.length - 1; i < polygon?.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const ConditionalWrapper = ({
  condition,
  wrapper,
  elseWrapper,
  children,
}) =>
  condition
    ? wrapper(children)
    : elseWrapper
    ? elseWrapper(children)
    : children;

/**
 * Check if a path has search params or not
 * @param {string} path
 * @returns {boolean}
 */
export const pathHasSearchParams = (path) => {
  const pathnameRegex = /[^?#]+/u;
  return !!path?.replace(pathnameRegex, "");
};

export const hasHinduNumerals = (str) => /[٠-٩]+/.test(str);

export const convertToArabicNumerals = (str) =>
  str.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

export const appendPathHash = (query, currentPath) => {
  const currentHash = currentPath?.split("#")[1] || "";
  const newHashObject = {
    ...qs.parse(decodeURI(currentHash)),
    ...query,
  };

  return `${qs.stringify(newHashObject)}`;
};

export const removeFromPathHash = (key, currentPath) => {
  const currentHash = currentPath.split("#")[1] || "";
  const hashObject = qs.parse(decodeURI(currentHash));
  delete hashObject[key];
  return `#${qs.stringify(hashObject)}`;
};

export const CloudfrontImageLoader = ({ src, width }) => {
  const DevicesWidthsMapper = {
    640: "250",
    750: "250",
    828: "300",
    1080: "300",
    1200: "350",
    1920: "350",
    2048: "350",
    3840: "350",
  };
  if (src.includes("http://") || src.includes("https://")) {
    return src;
  }
  return `${config.IMAGES_BASE_URL}webp/${DevicesWidthsMapper[width]}x0/props/${src}`;
};
